import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { paymentService, XpayCheckoutResult } from '../services/payments';
import { Container, Stack } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { PageHeader } from '../design-system/patterns/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { spacing } from '../design-system/tokens/spacing';

const ResultContainer = styled(Container)`
  max-width: 640px;
`;

const ResultCard = styled(Card)`
  padding: ${spacing[10]};
  text-align: center;
`;

const Message = styled(Text)`
  margin: ${spacing[4]} 0 ${spacing[8]};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  justify-content: center;
`;

const PaymentResult: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');
  const cancelled = searchParams.get('cancelled') === '1';

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<XpayCheckoutResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelledRequest = false;

    const load = async () => {
      if (!paymentId) {
        setError(t('payments.result.missingPayment'));
        setLoading(false);
        return;
      }

      if (cancelled) {
        setResult({
          paymentId,
          status: 'Cancelled',
          isPaid: false,
          message: t('payments.result.cancelledMessage'),
        });
        setLoading(false);
        return;
      }

      try {
        const data = await paymentService.getCheckoutResult(paymentId);
        if (!cancelledRequest) {
          setResult(data);
        }
      } catch (err) {
        if (!cancelledRequest) {
          console.error('Error verifying payment result:', err);
          setError(
            err instanceof Error ? err.message : t('payments.result.verifyError')
          );
        }
      } finally {
        if (!cancelledRequest) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelledRequest = true;
    };
  }, [paymentId, cancelled, t]);

  const title = result?.isPaid
    ? t('payments.result.successTitle')
    : cancelled
      ? t('payments.result.cancelledTitle')
      : t('payments.result.pendingTitle');

  return (
    <ResultContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader title={t('payments.result.pageTitle')} pattern={true} />
        <ResultCard variant="elevated" accentBorder="top">
          {loading ? (
            <>
              <LoadingSpinner size="lg" />
              <Message size="lg" color="secondary">
                {t('payments.result.verifying')}
              </Message>
            </>
          ) : (
            <>
              <Heading variant="h2" color="text">
                {error ? t('payments.result.errorTitle') : title}
              </Heading>
              <Message size="base" color="secondary">
                {error
                  || (result?.bundledYears && result.bundledYears.length > 1
                    ? t('payments.arrearsPaid', { years: result.bundledYears.join(' + ') })
                    : result?.message)
                  || t('payments.result.unknown')}
              </Message>
              <Actions>
                {result?.isPaid && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => navigate('/payments')}
                  >
                    {t('payments.result.viewPayments')}
                  </Button>
                )}
                {!result?.isPaid && paymentId && !cancelled && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        setError(null);
                        const checkout = await paymentService.startCheckout(paymentId);
                        window.location.href = checkout.hostedPageUrl;
                      } catch (err) {
                        setLoading(false);
                        setError(
                          err instanceof Error
                            ? err.message
                            : t('payments.checkoutError')
                        );
                      }
                    }}
                  >
                    {t('payments.payNow')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate('/payments')}
                >
                  {t('payments.result.backToPayments')}
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate('/')}
                >
                  {t('common.back') || 'Dashboard'}
                </Button>
              </Actions>
            </>
          )}
        </ResultCard>
      </Stack>
    </ResultContainer>
  );
};

export default PaymentResult;
